import React, {
  useRef,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  StyleSheet,
  PixelRatio,
  Pressable,
  View,
  Modal,
  Platform,
  ActivityIndicator,
} from 'react-native';

import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import BottomSheet, {
  BottomSheetBackdropProps,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import Styles from 'src/components/Styles';
import Localized from 'src/constants/AppStrings';
import BirthMonthYearInput from 'src/components/elements/rewards/BirthMonthYearInput';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountStore from 'src/stores/AccountStore';
import {alertError} from 'src/components/helpers/AlertHelper';
import uuid from 'src/nativeModules/UUID';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import {handleBackdropPressHelper} from 'src/components/screens/rewards/policyHelper';
import AppRoutes from 'src/AppRoutes';
import {
  rewardsStartPolicyInBackground,
  rewardsCancelPolicyInBackground,
  addSlash,
  validateMonthYearInput,
} from 'src/Util';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';
import KeyboardAvoidingView from 'src/components/elements/365KeyboardAvoidingView';
import CancelIcon from 'src/components/img/svg/CancelIcon';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import KeyboardAvoidingViewAndroid from './KeyboardAvoidingViewAndroid';

export type RewardsAddBirthdayProps = WithGlobalizeProps & {
  rewardProgramId: string;
};

enum BottomSheetState {
  NOT_READY = 'NOT_READY',
  FULLY_OPEN = 'FULLY_OPEN',
  FULLY_CLOSED = 'FULLY_CLOSED',
}

const RewardsAddBirthday: React.FC<RewardsAddBirthdayProps> = (props) => {
  const context = useContext(ScreenContext);

  const bottomSheetRef = useRef<BottomSheet>(null);
  const [onCloseCalled, setOnCloseCalled] = useState(false);
  const [bottomSheetCurrentState, setBottomSheetCurrentState] =
    useState<BottomSheetState>(BottomSheetState.NOT_READY);
  const [modalVisible, setModalVisible] = useState(Platform.OS === 'web');
  const [birthYear, setBirthYear] = useState<string>('');
  const [birthMonthYear, setBirthMonthYear] = useState<string>(''); // MM/YYYY
  const [birthMonthYearError, setBirthMonthYearError] = useState<string>('');
  const [refreshing, setIsRefreshing] = useState(false);

  const birthMonthYearPrevRef = useRef<string>('');

  const bottomSheetCustomStates = useMemo(
    () => ({
      [-1]: BottomSheetState.FULLY_CLOSED,
      0: BottomSheetState.FULLY_OPEN,
      999: BottomSheetState.NOT_READY,
    }),
    [],
  );

  useEffect(() => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'RewardsAddBirthday', {
      ...props,
    });
    if (bottomSheetRef.current) {
      bottomSheetRef.current.close();
    }
  }, [props]);

  const onClose = useCallback(() => {
    if (onCloseCalled) {
      return;
    }
    setOnCloseCalled(true);

    FirebaseAnalytic.trackEvent('onClose', 'AddCreditCard', {
      ...props,
      navigate: AppRoutes.RewardsScreen,
    });

    if (Platform.OS === 'web') {
      setModalVisible(false);
    } else if (bottomSheetRef.current) {
      bottomSheetRef.current.forceClose();
    }

    NavActions.pop();
  }, [onCloseCalled, props]);

  const handleSheetChange = (index: number) => {
    setBottomSheetCurrentState(bottomSheetCustomStates[index]);
    if (index === -1) {
      onClose();
    }
  };

  const handleBackdropPress = () => {
    if (bottomSheetCurrentState === BottomSheetState.NOT_READY) {
      NavActions.pop();
    } else if (bottomSheetRef.current) {
      bottomSheetRef.current.forceClose();
    }
  };

  const birthMonthYearSelected = (val: string) => {
    birthMonthYearPrevRef.current = birthMonthYear;
    const withSlash = addSlash(birthMonthYearPrevRef.current, val);
    const error = validateMonthYearInput(withSlash, 80, {
      birthMonthYearError: Localized.Errors.birthMonthYearError,
      birthMonthYearErrorExceedsYear:
        Localized.Errors.birthMonthYearErrorExceedsYear,
    });
    const [, year] = withSlash.split('/');
    setBirthMonthYear(withSlash);
    setBirthMonthYearError(error);
    setBirthYear(year || '');
  };

  const [month, year] = (birthMonthYear || '').split('/');
  const isBtnActive = Boolean(month && year && !birthMonthYearError);

  const saveBirthday = async () => {
    let rewardStarted = false;
    try {
      if (Platform.OS === 'web') {
        setIsRefreshing(true);
      }
      context?.actions?.showSpinner?.();
      const accountId = AccountStore.getAccountId();
      if (!isBtnActive) {
        return;
      }

      // Start policy first (so success path awards properly)
      await rewardsStartPolicyInBackground(
        accountId,
        props.rewardProgramId,
        AppRoutes.RewardsAddBirthday,
      );
      rewardStarted = true;

      // Parse to numbers
      const birthMonthNum = parseInt(month!, 10);
      const birthYearNum = parseInt(year!, 10);

      const response = await ActionsFactory.getAccountActions().updateInfo(
        accountId,
        {
          birthMonth:
            Number.isFinite(birthMonthNum) &&
            birthMonthNum >= 1 &&
            birthMonthNum <= 12
              ? birthMonthNum
              : undefined,
          birthYear: Number.isFinite(birthYearNum) ? birthYearNum : undefined,
        },
      );

      AccountStore.setDOB(birthMonthNum, birthYearNum);
      setIsRefreshing(false);
      FirebaseAnalytic.trackEvent('saveBirthday', 'RewardsAddBirthday', {
        birthMonth: birthMonthNum,
        birthYear: birthYearNum,
        response,
      });
      NavActions.navigate(AppRoutes.RewardsScreen, {
        refreshRewards: true,
      });
    } catch (error) {
      try {
        if (rewardStarted) {
          const accountId = AccountStore.getAccountId();
          await rewardsCancelPolicyInBackground(
            accountId,
            props.rewardProgramId,
            generateErrorMessage(error),
          );
        }
      } catch (cancelError) {
        CrashlyticsEvents.log(
          'Exception',
          'RewardsAddBirthday:cancelPolicyOnFailure',
          generateErrorMessage(cancelError),
        );
        Events.Error.trackEvent(
          'Exception',
          'RewardsAddBirthday:cancelPolicyOnFailure',
          generateErrorMessage(cancelError),
        );
      }

      setIsRefreshing(false);
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'RewardsAddBirthday:saveBirthday',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'RewardsAddBirthday:saveBirthday',
        generateErrorMessage(error),
        guid,
      );
      alertError(Localized.Errors.unable_to_save_changes, guid);
    } finally {
      setIsRefreshing(false);
      context?.actions?.hideSpinner?.();
    }
  };

  const handlePress = React.useCallback(() => {
    handleBackdropPressHelper({onClose, bottomSheetRef});
  }, [onClose]);

  const renderBackdropComponent = (backDropProps: BottomSheetBackdropProps) => (
    <Pressable style={backDropProps.style} onPress={handleBackdropPress} />
  );

  const renderBottomSheet = () => (
    <BottomSheet
      ref={bottomSheetRef}
      snapPoints={[300 + PixelRatio.getFontScale() * 20]}
      enableHandlePanningGesture
      onChange={handleSheetChange}
      enablePanDownToClose
      backdropComponent={renderBackdropComponent}
      style={styles.bottomSheet}
    >
      <BottomSheetScrollView keyboardShouldPersistTaps="handled">
        <BirthMonthYearInput
          birthYear={birthYear}
          birthMonthYear={birthMonthYear}
          birthMonthYearError={birthMonthYearError}
          onChangeText={birthMonthYearSelected}
          buttonAction={saveBirthday}
          _isBtnActive={isBtnActive}
        />
      </BottomSheetScrollView>
    </BottomSheet>
  );

  const renderModal = () => (
    <Modal
      animationType="fade"
      transparent
      visible={modalVisible}
      onRequestClose={onClose}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalContainer}>
          <AVTouchableOpacity
            aria-label={`X icon, Double tap to ${Localized.Buttons.cancel} offer details`}
            role="button"
            style={styles.closeModalContainer}
            onPress={onClose}
            accessible
            accessibilityLabel={Localized.Buttons.cancel}
            accessibilityRole="button"
            hitSlop={Styles.HitSlop_10}
          >
            <View style={styles.closeModalTitleContainer}>
              <CancelIcon />
            </View>
          </AVTouchableOpacity>
          {refreshing ? (
            <View style={styles.loader}>
              <ActivityIndicator />
            </View>
          ) : (
            <BirthMonthYearInput
              birthYear={birthYear}
              birthMonthYear={birthMonthYear}
              birthMonthYearError={birthMonthYearError}
              onChangeText={birthMonthYearSelected}
              buttonAction={saveBirthday}
              _isBtnActive={isBtnActive}
            />
          )}
        </View>
      </View>
    </Modal>
  );

  const KeyBoardAvoiding =
    Platform.OS === 'ios' ? KeyboardAvoidingView : KeyboardAvoidingViewAndroid;

  return (
    <View style={styles.container}>
      <KeyBoardAvoiding style={Styles.Style.flex} behavior="padding" insideTab>
        <Pressable style={styles.backdropPressable} onPress={handlePress} />
        {Platform.OS === 'web' ? renderModal() : renderBottomSheet()}
      </KeyBoardAvoiding>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor:
      Platform.OS === 'web' ? 'transparent' : Styles.bottomTreyBackgroundColor,
  },
  backdropPressable: {
    width: '100%',
    minHeight: '60%',
    backgroundColor: 'transparent',
  },
  bottomSheet: {
    borderRadius: Styles.Spacing.m4,
    overflow: 'hidden',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: Styles.Spacing.m3,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 600,
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m3 + Styles.Spacing.m2,
    overflow: 'hidden',
    padding: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  loader: {minHeight: '60%'},
  closeModalContainer: {
    position: 'absolute',
    top: Styles.Spacing.m1,
    right: Styles.Spacing.m1,
    width: Styles.Heights.touchTargetHeight2,
    height: Styles.Heights.touchTargetHeight2,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeModalTitleContainer: {
    width: Styles.Spacing.m2 + Styles.Spacing.m3,
    height: Styles.Spacing.m2 + Styles.Spacing.m3,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default withForwardedNavigationParams()(
  withGlobalize(RewardsAddBirthday),
);
